/**
 * File: admin_page.tsx
 * Description: Admin interface page for Khora staff to manage FAQ entries. Renders a two-column
 * layout: the left column lets staff search and delete existing articles; the right column
 * provides a form to create new ones. Accessible only via the /admin route, enforced at the Nginx level.
 */

import { useState } from "react";
import {
  Box,
  Typography
} from '@mui/material';
import type { Category, CategorySelection } from "@/interfaces/category";
import Header from "@/components/global/header";
import CategoryManager from "@/components/admin/category_manager";
import SelectNewArticleTitle from "@/components/admin/select_new_article_title";
import MarkdownEditor from "@/components/admin/markdown_editor";
import ButtonCreateArticle from "@/components/admin/button_create_article";
import SearchArticles from "@/components/global/search_articles";
import DeleteFilterArticleList from "@/components/admin/delete_filter_article_list";
import { ENDPOINT_ADMIN_GET_ARTICLES } from '@/constants/endpoints_urls';
import { useCategoriesFetch } from "@/hooks/use_categories_fetch";
import { createArticle, deleteArticle } from "@/api_calls/articles";

/**
 * Admin page component that orchestrates the full FAQ management workflow.
 * Fetches all categories with their articles on mount and exposes state
 * shared between the search/delete panel and the create-article form.
 *
 * @returns {JSX.Element} The full admin page layout with header and two management panels.
 */
function AdminPage() {

  const { categories, isLoading, refetch } = useCategoriesFetch(ENDPOINT_ADMIN_GET_ARTICLES);

  const [categorySelection, setCategorySelection] = useState<CategorySelection | null>(null);
  const [newArticleTitle, setNewArticleTitle] = useState('');
  const [newArticleContent, setNewArticleContent] = useState('');
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  /**
   * Updates the selected category for the create-article form.
   *
   * @param {CategorySelection | null} selection - The new category selection, or null when cleared.
   * @returns {void}
   */
  function handleCategoryChange(selection: CategorySelection | null) {
    setCategorySelection(selection);
  }

  /**
   * Updates the title field for the article being created.
   *
   * @param {string} title - The new title value typed by the user.
   * @returns {void}
   */
  function handleNewArticleTitleChange(title: string) {
    setNewArticleTitle(title);
  }

  /**
   * Updates the markdown content field for the article being created.
   *
   * @param {string} content - The new markdown content value from the editor.
   * @returns {void}
   */
  function handleNewArticleContentChange(content: string) {
    setNewArticleContent(content);
  }

  /**
   * Receives the filtered category subset from SearchArticles and stores it
   * so FilterArticleList can render only the matching entries.
   *
   * @param {Category[]} filtered - The categories that match the current search selection.
   * @returns {void}
   */
  function handleFilter(filtered: Category[]) {
    setFilteredCategories(filtered);
  }

  /**
   * Resets the create-article form fields and re-fetches the full FAQ data
   * after an article has been successfully created or deleted.
   *
   * @returns {void}
   */
  function handleSuccess() {
    setCategorySelection(null);
    setNewArticleTitle('');
    setNewArticleContent('');
    void refetch();
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
      <Header />
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
        }}
      >
        {
          isLoading ?
          <></>
          :
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '50%', p: 3 }}>
              <Typography variant="h6">Eliminar Artículo</Typography>
              <SearchArticles
                categories={categories}
                onFilter={handleFilter}
              />
              <DeleteFilterArticleList
                categories={filteredCategories}
                onDeleteArticle={deleteArticle}
                onSuccess={handleSuccess}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '50%', p: 3 }}>
              <Typography variant="h6">Añadir Artículo</Typography>
              <CategoryManager
                categories={categories}
                onChange={handleCategoryChange}
              />
              <SelectNewArticleTitle
                value={newArticleTitle}
                onChange={handleNewArticleTitleChange}
                selectedCategory={categories.find((category) => category.id === categorySelection?.categoryId) ?? null}
              />
              <MarkdownEditor
                value={newArticleContent}
                onChange={handleNewArticleContentChange}
              />
              <ButtonCreateArticle
                categorySelection={categorySelection}
                newArticleTitle={newArticleTitle}
                newArticleContent={newArticleContent}
                onCreateArticle={createArticle}
                onSuccess={handleSuccess}
              />
            </Box>
          </>
        }
      </Box>
    </Box>
  );
}

export default AdminPage;
